/**
 * Turkish — the source of truth.
 *
 * Written first, not translated. Every claim in this file is checked against
 * `docs/ACCEPTANCE_MATRIX.md`: if a sentence asserts a capability, there is a
 * PASS row behind it, and the `proof.*` key beside it names the command that
 * proves it. The project's own ethos is that its "known gaps" sections are the
 * honest part; a landing page is exactly where that gets quietly abandoned, so
 * the rule here is explicit — no sentence outruns the code.
 *
 * **The voice is formal *siz*, and that is a change from the console's.** The
 * console says *sen* to the one operator standing in front of it, which is
 * right for a tool somebody lives in all day. This site is read by a person
 * deciding whether their organisation will buy it, often before they have met
 * anybody here, and Turkish business register makes *sen* read as a startup
 * addressing a hobbyist. Mixing the two would be worse than either, so the six
 * sentences that addressed the reader informally were converted rather than
 * left: `voice.test.ts` fails on an informal second person anywhere in this
 * file.
 *
 * Machine values — host names, ids, commands, rejection codes — are NOT in this
 * catalogue; they live in `data/fixtures.ts`, because a machine value is not a
 * translatable string. Neither is a postal address or a telephone number: those
 * are in `data/links.ts`, because a street does not change language.
 */
export const tr = {
  /* ---- Document ------------------------------------------------------ */
  'meta.title': 'Kimene — yapay zekâ iş gücü kontrol düzlemi',
  'meta.description':
    'Havuzlar sizde kalır: hesaplar, modeller, sunucular, projeler. İşi hangisinin yürüteceğine Kimene karar verir, kararı gerekçesiyle yazar ve işi kendi makinelerinizde gerçek sağlayıcı CLI’larıyla çalıştırır.',
  'meta.ogAlt': 'Kimene — havuzlardan karara giden yönlendirme şeması',

  /* Sayfa başına başlık ve açıklama. Beş belgenin tek bir başlığı paylaşması,
     tarayıcıya beş kopya sayfa, paylaşım önizlemesine de beş özdeş kart olarak
     görünür — ki bu, hiç üstveri olmamasından daha kötüdür: kasıtlı durur. */
  'meta.product.title': 'Ürün — Kimene',
  'meta.product.description':
    'Havuzlar, yönlendirme kararı, gerçek makinelerde yürütme, kanıt sözleşmesi, onay kapısı, oturum sürekliliği ve filo görünürlüğü. Her bölüm, iddiasını kanıtlayan komutu da yazar.',
  'meta.security.title': 'Güvenlik ve mimari — Kimene',
  'meta.security.description':
    'Kontrol düzlemi hiçbir kimlik bilgisini taşımaz; yalnızca yol ve referans tutar. Güven sınırının nerede geçtiği, neyin kayıt altına alındığı ve kurulumun açıkça kabul ettiği boşluklar.',
  'meta.company.title': 'Kurumsal — Kimene',
  'meta.company.description':
    'Kimene’yi Arpanet Bilişim Teknolojileri A.Ş. geliştirir; ekip Ankara’da ODTÜ Teknokent’te çalışır. Ürünün üzerine kurulduğu üç kural ve kurumsal bilgiler.',
  'meta.contact.title': 'İletişim — Kimene',
  'meta.contact.description':
    'Demo talebi, kurulum ve fiyatlandırma için doğrudan iletişim. Ankara ODTÜ Teknokent’te geliştirilen kontrol düzlemi. Telefon: {phone}',

  /* ---- Chrome -------------------------------------------------------- */
  'nav.skip': 'İçeriğe geç',
  'nav.home': 'Ana sayfa',
  'nav.product': 'Ürün',
  'nav.security': 'Güvenlik',
  'nav.company': 'Kurumsal',
  'nav.contact': 'İletişim',
  'nav.console': 'Konsola gir',
  'nav.locale': 'Dil',
  'nav.localeOther': 'English',
  'nav.localeSwitch': 'Sayfayı {language} diline çevir',
  'nav.menuOpen': 'Menüyü aç',
  'nav.menuClose': 'Menüyü kapat',
  'nav.site': 'Site gezinmesi',
  'nav.themeToLight': 'Açık temaya geç',
  'nav.themeToDark': 'Koyu temaya geç',

  /* Shared chrome words inside the mockups. Turkish sentences, not machine
     values — the codes themselves stay in the fixtures. */
  'ui.live': 'canlı',
  'ui.waiting': 'bekliyor',
  'ui.done': 'bitti',
  'ui.unmeasured': 'ölçülmedi',
  'ui.immutable': 'değiştirilemez',
  'ui.selected': 'seçilen',
  'ui.rejected': 'elenen',
  'ui.total': 'toplam',
  'ui.components': 'bileşenler',
  'ui.penalties': 'cezalar',
  'ui.weight': 'ağırlık',
  'ui.approve': 'Onayla',
  'ui.reject': 'Reddet',
  'ui.real': 'gerçek',
  'ui.simulated': 'benzetim',
  'ui.behind': 'geride',
  'ui.proof': 'kanıt',
  'ui.onThisPage': 'Bu sayfada',
  'ui.readMore': 'Ayrıntı',

  /* ---- 1 · Hero ------------------------------------------------------ */
  'hero.eyebrow': 'Kendi sunucunuzda çalışan kontrol düzlemi',
  'hero.title': 'Yapay zekâ iş gücünüz.',
  'hero.deck': 'Tek rota, tek karar, tek kayıt.',
  'hero.sub':
    'Havuzlar sizde kalır: hesaplar, modeller, sunucular, projeler, roller. İşi hangisinin yürüteceğine Kimene karar verir, kararı gerekçesiyle yazar ve işi gerçek makinelerde gerçek CLI’larla çalıştırır.',
  'hero.ctaPrimary': 'Demo talep edin',
  'hero.ctaSecondary': 'Konsola gir',
  'hero.fig.pools': 'Havuzlar',
  'hero.fig.eligibility': 'Uygunluk',
  'hero.fig.weights': 'Ağırlıklar',
  'hero.fig.decision': 'Karar',
  'hero.fig.route': 'Rota',
  'hero.fig.record': 'Kayıt',
  'hero.fig.alt':
    'Beş havuzdan gelen adaylar bir uygunluk kapısından geçiyor; yedisi gerekçesiyle eleniyor, kalanlar ağırlıklarla puanlanıyor, tek bir karar düğümü bir rota ve değiştirilemez bir kayıt üretiyor.',

  /* ---- 2 · Havuzlar -------------------------------------------------- */
  'pools.eyebrow': 'Havuzlar',
  'pools.title': 'Hiçbir varlık bir diğerinin sahibi değildir.',
  'pools.sub':
    'Projeye sabitlenmiş hesap yok, hesaba sabitlenmiş model yok. Eşleşme yürütme anında olur, o tek koşu için geçerli bir rota olarak yazılır ve iş bitince çözülür.',
  'pools.body.1':
    'Bir proje tercih, izin ve rota kipi söyler. Yönlendirici bunları canlı kapasite, sağlık ve geçmişle birleştirir. Ortaya çıkan bağ yapılandırma değil, kayıttır.',
  'pools.pull': 'Kimene kendi kotasını icat etmez.',
  'pools.body.2':
    'Sağlayıcı kapasitesi, eşzamanlılık politikası, kaynak kiralaması ve öncelik dört ayrı şeydir. Birbirine karıştırılan bu dördü, “kota” diye tek kelimeye sıkıştırıldığında operatör hangi sınıra çarptığını bir daha asla bilemez.',
  'pools.proof': 'go test ./internal/domain — alanda hiçbir kota alanı yok',
  'pools.fig.forbidden': 'yok',
  'pools.fig.run1': 'birinci koşu',
  'pools.fig.run2': 'ikinci koşu',
  'pools.fig.alt':
    'Beş havuz, aralarında kalıcı bir bağ olmadan yan yana. Aynı projenin iki görevi iki farklı hesaba yönleniyor ve her biri kendi yürütme kaydını yazıyor.',

  /* ---- 3 · Karar kaydı ----------------------------------------------- */
  'decision.eyebrow': 'Karar kaydı',
  'decision.title': '“Yapay zekâ öyle seçti” bir açıklama değildir.',
  'decision.sub':
    'Önce sert uygunluk elemesi, sonra ağırlıklı puanlama. Seçilen aday kadar elenenler de gerekçeleriyle birlikte değiştirilemez bir karara yazılır.',
  'decision.body.1':
    'Kayıt; seçilen rotayı, değerlendirilen ve elenen adayları, her eleme gerekçesini, puanın her bileşenini, danışılan kapasite anlık görüntülerini ve varsa operatörün geçersiz kılmasını taşır.',
  'decision.pull': 'toplam = Σ bileşen − Σ ceza. Üçüncü bir yol yok.',
  'decision.body.2':
    'Rota simülatörü aynı puanlamayı varsayımsal girdilerle yeniden oynatır: kira almadan, oturum başlatmadan. “Şu sunucu düşseydi ne olurdu” sorusunun cevabı tahmin değil, hesaptır.',
  'decision.proof': 'go test ./internal/routing — kendini açıklayamayan bir kararı reddeder',
  'decision.fig.title': 'Rota kararı',
  'decision.fig.why': 'Neden bu hesap?',
  'decision.fig.toggle': 'Sunucuyu çevrimdışı işaretle',
  'decision.fig.alt':
    'Bir yönlendirme kaydı: altı puan bileşeni, dört ceza, toplam, ve gerekçeleriyle elenmiş yedi aday.',

  /* ---- 4 · Yürütme --------------------------------------------------- */
  'exec.eyebrow': 'Yürütme',
  'exec.title': 'İş, gerçek makinelerde, gerçek CLI’larda çalışır.',
  'exec.sub':
    'Kontrol düzlemi hiçbir sağlayıcı CLI’ına doğrudan konuşmaz. Her oturum, o makinede yaşayan düğüm ajanının başlattığı bir tmux oturumunun içindedir.',
  'exec.body.1':
    'Bu dolaylılık, tarayıcı yenilemesini, sunucu dağıtımını ve kopan bir SSH bağlantısını süren işe zararsız kılan şeydir.',
  'exec.durability': 'Tarayıcıyı kapat. Sunucuyu dağıt. SSH’ı düşür. Oturum ayakta.',
  'exec.body.2':
    'Yeniden bağlandığında ekran baştan çizilmez; bıraktığın imleçten devam eder. Gönderdiğin her tuş tam olarak bir kez uygulanır — ne yutulur, ne iki kez yazılır.',
  'exec.proof': 'make test-live-terminal — çalışan bir döngü, yöneticisi tamamen kapatılınca da yaşıyor',
  'exec.fig.browserClosed': 'tarayıcı kapandı',
  'exec.fig.serverDeployed': 'sunucu yeniden başladı',
  'exec.fig.sshDropped': 'SSH düştü',
  'exec.fig.alt':
    'Üç terminal döşemesi: biri yazıyor, biri girdi bekliyor, biri bitmiş. Altlarında kesintisiz bir oturum çizgisi, üzerinde üç kesinti işareti.',

  /* ---- 5 · Kanıt ----------------------------------------------------- */
  'evidence.eyebrow': 'Kanıt',
  'evidence.title': '“Bitti” ajanın söylediği şey değildir.',
  'evidence.sub':
    'Tamamlandı durumuna yalnızca inceleme ya da onay bekliyor üzerinden geçilir; bunu tek bir geçiş tablosu zorlar. API’de bir görev durumunu doğrudan yazacak hiçbir yol yoktur.',
  'evidence.body.1':
    'Her görev türü kendi kanıt sözleşmesini taşır. Bir kod görevi fark, test, lint ve derleme raporu ile bir inceleyici kararı ister; bir araştırma görevi erişim tarihli kaynaklar ister.',
  'evidence.pull': 'Kimene sizin adınıza test komutu icat etmez.',
  'evidence.body.2':
    'Tanımlamadığın kapı kapalı kalır ve neden kapalı olduğunu söyler. Her rapor onu üreten komutu taşır; artefaktlar değiştirilemez, içerik adresli ve özetiyle doğrulanır.',
  'evidence.proof':
    'go test ./internal/domain ./internal/artifact — tamamlandı yalnızca inceleme ya da onay üzerinden erişilebilir',
  'evidence.fig.contract': 'Kanıt sözleşmesi',
  'evidence.fig.missing': 'komut tanımlı değil',
  'evidence.fig.define': 'Komutu tanımla',
  'evidence.fig.locked': 'geçiş kapalı',
  'evidence.fig.alt':
    'Dört kanıt kartı: fark, test ve derleme onaylı; lint kartı, komutu tanımlı olmadığı için eksik. Tamamlanma geçişi kapalı duruyor.',

  /* ---- 6 · Onay ------------------------------------------------------ */
  'approval.eyebrow': 'Onay',
  'approval.title': 'İnsanın imzalaması gereken şeyi insan imzalar.',
  'approval.sub':
    'Risk seviyesi koddan gelir, istemden değil. Yüksek riskli işler bir kişiye gider ve operatör isterse bu onay ikinci faktör ister.',
  'approval.body.1':
    'Onay kartı ne istendiğini, hangi kuralın tetiklendiğini, hangi kaynakların etkileneceğini ve reddedilirse ne olacağını söyler. Karar, kim verdiğiyle birlikte kaydedilir.',
  'approval.brakeTitle': 'Gözetimsizken şef yalnızca frenler.',
  'approval.brakeSub':
    'Kimse izlemiyorken uygulanabilecek eylemler işi durduranlar ve bir kişiyi çağıranlardır. İş başlatan ya da abonelik harcayan hiçbir eylem, bir insan olmadan uygulanmaz.',
  'approval.brakeCan': 'gözetimsiz uygulanabilir',
  'approval.brakeCannot': 'bir kişiyi bekler',
  'approval.proof': 'go test ./internal/approval — kendi işini onaylamak yapısal olarak imkânsız',
  'approval.fig.alt':
    'Bir onay kartı ve yanında altı haneli ikinci faktör alanı; altında gözetimsiz uygulanabilen ve bir kişiyi bekleyen eylemlerin listesi.',

  /* ---- 7 · Süreklilik ------------------------------------------------ */
  'continuity.eyebrow': 'Süreklilik',
  'continuity.title': 'Geç kalmış bir tutucu, etki uygulayamaz.',
  'continuity.sub':
    'Her kiralama artan bir çit jetonu taşır. Korunan kaynak gördüğü en yüksek jetonu saklar ve daha düşüğünü reddeder.',
  'continuity.body.1':
    'Kiralar veri kaybedebileceği açıkça kabul edilen bir yerde yaşıyor. Bu ikilinin güvenli olmasının tek sebebi çit: Kimene tek bir önbelleğin veremeyeceği bir garantiyi iddia etmez, daha zayıf ve doğru olanı iddia eder.',
  'continuity.boundaryTitle': 'Geçiş yalnızca güvenli sınırda olur.',
  'continuity.boundarySub':
    'Tur bitti, kontrol noktası yazıldı, insan bekleniyor ya da doğrulanmış bir hız sınırı. Araç çağrısının, yazmanın, göçün ya da dağıtımın ortasında asla.',
  'continuity.pull': 'Geçici olan, kalıcı değildir.',
  'continuity.body.2':
    'İki saniyelik bir kesinti sağlayıcı arızası sayılmaz; yerinde yeniden denenir. Yalnızca tiplenmiş ve doğrulanmış bir hız sınırı, kimlik hatası ya da erişilemeyen sunucu rotayı değiştirir.',
  'continuity.proof':
    'make test-integration-live — 32 yönlü çekişmede tam olarak bir kazanan; ölü tutucunun yenilemesi reddedilir',
  'continuity.fig.refused': 'reddedildi',
  'continuity.fig.alt':
    'Bir zaman çizelgesi: kirayı 17 jetonuyla alan tutucu duraklıyor, kira düşüyor, ikinci tutucu 18 ile yazıyor, ve ilkinin geç kalmış yazması reddediliyor.',

  /* ---- 8 · Filo ------------------------------------------------------ */
  'fleet.eyebrow': 'Filo',
  'fleet.title': 'Ölçülmedi, sıfır değildir.',
  'fleet.sub':
    'Ölçülmemiş kapasitenin kendi rengi ve kendi deseni var. Kimene onu asla bir sayı gibi çizmez, çünkü boş bir bar ile sıfır bir bar aynı görünüp zıt şeyler söyler.',
  'fleet.body.1':
    'Aynı ret puanlamada da geçerli: bilinmeyen kapasite iyimser bir 1,0 değildir. Kayıt, ölçüm ve tahmin birbirinden ayrı tutulur.',
  'fleet.degradationTitle': 'Bağımlılık düştüğünde ne olacağı yazılı.',
  'fleet.degradationSub':
    'Bu tablonun hiçbir satırında “eski veriyi taze gibi göster” yazmaz.',
  'fleet.proof':
    'go test ./internal/domain — kapasite bir işaretçi; bilinmiyor temsil edilebilir ve sıfır gibi okunamaz',
  'fleet.fig.cpu': 'İşlemci',
  'fleet.fig.ram': 'Bellek',
  'fleet.fig.disk': 'Disk',
  'fleet.fig.gpu': 'GPU',
  'fleet.fig.alt':
    'Dört sunucu satırı; üçünün ölçerleri dolu, birinin GPU ölçeri taralı ve “ölçülmedi” yazıyor.',

  /* ---- 9 · Güvenlik -------------------------------------------------- */
  'security.eyebrow': 'Güvenlik',
  'security.title': 'Kontrol düzlemi hiçbir kimlik bilgisi tutmaz.',
  'security.sub':
    'Kimlikler, sahibi olan makinede kalır; veritabanı yalnızca yolu ve referansı bilir. Veritabanını ele geçirmek hiçbir sağlayıcı kimliği vermez.',
  'security.body.1':
    'Sağlayıcı yalıtımı işlem ortamı üzerinden yapılır, komut satırı üzerinden değil: süreç listesi herkese açıktır, ortam değildir. Düğümler tek kullanımlık bir kayıt jetonundan sonra karşılıklı TLS ile konuşur.',
  'security.pull': 'İç ağda olmak bir güvenlik önlemi değildir.',
  'security.body.2':
    'Redaksiyon çağrı yerinde değil, günlük işleyicisinin içindedir — unutulabileceği bir yerde durmaz. Terminal çıktısı hiçbir zaman ham HTML olarak basılmaz ve tarayıcı hiçbir makineye doğrudan bağlanmaz.',
  'security.point.mtls': 'Kayıt sonrası her bayt karşılıklı TLS ile',
  'security.point.hostLocal': 'Kimlik bilgisi sahibi makinede kalır',
  'security.point.redaction': 'Redaksiyon atlatılamaz',
  'security.point.proxied': 'Tarayıcı hiçbir sunucuya doğrudan bağlanmaz',
  'security.proof': 'go test ./internal/security/redact — redaksiyon günlük işleyicisinde',
  'security.fig.alt':
    'Üç bölge: tarayıcı, kontrol düzlemi ve sunucu. Kimlik bilgisi sunucunun içinden çıkmıyor; sınırı geçen bir günlük satırının gizli kısmı geçerken siliniyor.',

  /* ---- 10 · Kurulum -------------------------------------------------- */
  'install.eyebrow': 'Kurulum',
  'install.title': 'Dört bağımlılık, dört binary, bir doctor.',
  'install.sub':
    'Ubuntu, Go ve Node. Veritabanı, kuyruk, önbellek ve bir mesaj aracısı. doctor hepsini hiçbir şeyi bozmadan doğrular ve hangi kipte çalıştığını söyler.',
  'install.body.1':
    'Kurtarma yordamı çalışma kipine göre değişir, o yüzden doctor kipi de söyler. Konteyner yok, orkestratör yok, çalıştırmadan önce her hedefe kurulması gereken bir çalışma zamanı yok.',
  'install.firstRun':
    'İlk kurulumda bir adım sizi bekler: bir hesap ile model eşleşmesi doğrulanana kadar hiçbir çift yönlendirilebilir değildir. Bu, doğrulanmamış bir eşleşmeye iş göndermemek için bilerek böyledir.',
  'install.gapsTitle': 'Bilinen eksikler.',
  'install.gapsSub':
    'Her belgede bir “bilinen eksikler” bölümü var. Dürüst kısım orası; bir yeteneğe güvenmeden önce onu oku.',
  'install.gap.backup': 'Zamanlanmış yedek yok — komutlar var, onları çalıştıran bir zamanlayıcı yok.',
  'install.gap.nodeBootstrap': 'Tek komutla düğüm kurulumu yok; ikili elle kopyalanıp kaydediliyor.',
  'install.gap.tls': 'Sunucu kurulum betiği TLS ve ters vekil için hiçbir şey yapmıyor; ikisi de elle.',
  'install.gap.retention': 'Saklama politikası tohumlanıyor ama hiçbir şey onu uygulamıyor.',
  'install.gap.retry': 'Geçici bir hatayla düşen işleyici yeniden denenmiyor, ölü mektuba gidiyor.',
  'install.gap.terminal': 'Terminal katmanı henüz canlı bir tmux’a karşı çalıştırılmadı.',
  'install.proof': 'make doctor — dört bağımlılığı da tahribatsız doğrular',

  /* Bozulma matrisi — docs/ARCHITECTURE.md §14'ten birebir. */
  'fleet.degr.mongodb':
    'Yazma durur; okuma taze gibi önbellekten sunulmaz; açıkça erişilemez durumu verilir.',
  'fleet.degr.rabbitmq':
    'Arayüz ve durum okunabilir; yeni eşzamansız iş yerel giden kutusunda birikir; sağlık bozulmuş görünür.',
  'fleet.degr.redis':
    'Dağıtık işlemler kısıtlanır; kritik kiralar kapalı düşer; açık uyarı verilir.',
  'fleet.degr.mqtt':
    'Düğüm kalp atışı kontrol düzlemi WebSocket’ine geri düşer; telemetri bozulmuş etiketlenir.',

  /* Güvenli sınır — sağ sütun; kodda karşılığı olmayan, tarif edilen yerler. */
  'continuity.unsafe.tool': 'araç çağrısının ortasında',
  'continuity.unsafe.write': 'dosya yazmanın ortasında',
  'continuity.unsafe.migration': 'göçün ortasında',
  'continuity.unsafe.deploy': 'dağıtımın ortasında',
  'continuity.unsafe.paste': 'yapıştırmanın ortasında',

  /* ---- Ana sayfa: dört sütun ----------------------------------------- */
  'home.pillars.eyebrow': 'Ne yapar',
  'home.pillars.title': 'Dört şey, tek düzlem.',
  'home.pillars.sub':
    'Kimene havuzları tutar, işi hangisinin yürüteceğine karar verir, işi gerçek makinelerde çalıştırır ve olan biteni kanıtıyla birlikte yazar. Her biri için ayrıntı, kanıtıyla birlikte bir bölüm daha derinde.',

  'home.pillar.pools.title': 'Havuzlar sizde kalır',
  'home.pillar.pools.body':
    'Hesaplar, modeller, sunucular, projeler, roller ve araçlar ayrı havuzlardır. Hiçbiri diğerine ait değildir; eşleşme yalnızca iş çalışırken kurulur ve kayda geçer.',
  'home.pillar.decision.title': 'Karar gerekçesiyle yazılır',
  'home.pillar.decision.body':
    'Hangi hesabın, hangi modelin ve hangi sunucunun seçildiği; hangilerinin neden elendiği. Puanın bileşenleri değiştirilemez bir kayıt olarak durur.',
  'home.pillar.evidence.title': 'Tamamlandı, kanıt ister',
  'home.pillar.evidence.body':
    'Bir görev yalnızca inceleme ya da onay üzerinden tamamlanabilir. Kanıt sözleşmesi görev türünün kendisinden gelir; API’de durumu doğrudan yazacak bir yol yoktur.',
  'home.pillar.boundary.title': 'Kimlik bilgisi sınırda kalır',
  'home.pillar.boundary.body':
    'Kontrol düzlemi kimlik bilgisi gövdesi tutmaz, makineler arasında taşımaz. Yalnızca yolları ve referansları bilir; sağlayıcı yalıtımı süreç ortamıyla kurulur.',

  /* ---- Ana sayfa: vitrin --------------------------------------------- */
  'home.showcase.eyebrow': 'Gerçek olan kısım',
  'home.showcase.title': 'Soyut değil: açık oturumlar, gerçek makineler.',
  'home.showcase.sub':
    'Her oturum bir sunucuda, sağlayıcının kendi komut satırı aracıyla çalışır. Duvar bunları yan yana gösterir; bir operatör istediği anda klavyeyi devralır.',
  'home.showcase.link': 'Yürütmenin nasıl çalıştığını okuyun',

  /* ---- Ana sayfa: kurumsal şerit ------------------------------------- */
  'home.company.eyebrow': 'Geliştiren',
  'home.company.sub':
    'Kimene’yi ODTÜ Teknokent’te çalışan bir mühendislik ekibi geliştirir ve destekler.',
  'home.company.link': 'Kurumsal bilgiler',

  /* ---- Ürün sayfası --------------------------------------------------- */
  'product.eyebrow': 'Ürün',
  'product.title': 'Yedi bölüm, yedi kanıt.',
  'product.sub':
    'Aşağıdaki her bölüm tek bir iddia kurar ve o iddiayı kanıtlayan komutu yazar. Sıralama, bir işin Kimene içinde izlediği yolla aynıdır: havuzdan karara, karardan yürütmeye, yürütmeden kanıta.',

  /* ---- Güvenlik sayfası ----------------------------------------------- */
  'securityPage.eyebrow': 'Güvenlik ve mimari',
  'securityPage.title': 'Neyi teslim ediyorsunuz, neyi etmiyorsunuz.',
  'securityPage.sub':
    'Bu sayfa iki soruyu yanıtlar: kontrol düzlemi neye erişir, ve bir şey ters gittiğinde ne olur. Kurulum bölümü de buradadır, çünkü “kendi makinelerinizde çalışır” bir başlangıç adımından önce bir güvenlik olgusudur.',

  /* ---- Kurumsal sayfası ----------------------------------------------- */
  'companyPage.eyebrow': 'Kurumsal',
  'companyPage.title': 'Kimene’yi kim geliştiriyor.',
  'companyPage.sub':
    'Kendi sunucunuzda çalışan ve sağlayıcı hesaplarınızı yöneten bir yazılım, bir yerden satın alınır. Burada o yer yazılıdır.',

  'company.who.body.1':
    'Kimene, ODTÜ Bilişim İnovasyon Merkezi’nde çalışan bir mühendislik ekibi tarafından geliştirilir ve desteklenir. Ürün, kendi altyapınıza kurulur: veriler sizin sunucunuzda kalır, sağlayıcı hesapları sizin hesaplarınızdır.',
  'company.who.pull': 'Yazılımı siz çalıştırırsınız; biz kurar, uyarlar ve destekleriz.',
  'company.who.body.2':
    'Kurulum, entegrasyon ve sürüm yükseltmeleri için doğrudan iletişim kurabilirsiniz. Kimene kapalı kaynaklı bir üründür; lisanslama ve destek koşulları görüşmeyle belirlenir.',

  'company.fact.legal': 'Ticari unvan',
  'company.fact.address': 'Adres',
  'company.fact.email': 'E-posta',
  'company.fact.phone': 'Telefon',

  'company.principles.eyebrow': 'Ürünün kuralları',
  'company.principles.title': 'Üç kural, üçü de kodda.',
  'company.principles.sub':
    'Bunlar slogan değil, yazılımın uymak zorunda olduğu kısıtlardır. Üçünün de karşılığını ürün sayfasında, kanıt komutuyla birlikte görebilirsiniz.',
  'company.p1.title': 'Hiçbir havuz diğerine ait değildir',
  'company.p1.body':
    'Projeye bağlı hesap, hesaba bağlı model yoktur. Eşleşme yalnızca iş çalışırken kurulur ve değiştirilemez bir karar kaydı olarak yazılır.',
  'company.p2.title': 'Ölçülmemiş, sıfır değildir',
  'company.p2.body':
    'Kapasite ölçülemediğinde bir sayı uydurulmaz. Bilinmeyen kendi rengiyle görünür; “muhtemelen iyidir” diye bir durum arayüze hiç ulaşmaz.',
  'company.p3.title': 'Tamamlandı, kanıt ister',
  'company.p3.body':
    'Bir görev yalnızca inceleme ya da onay üzerinden tamamlanabilir. Bu geçiş tablosuyla zorlanır; kısayol eklenemez.',
  'company.principles.link': 'Üçünü de ürün sayfasında görün',

  /* ---- İletişim sayfası ----------------------------------------------- */
  'contactPage.eyebrow': 'İletişim',
  'contactPage.title': 'Konuşalım.',
  'contactPage.sub':
    'Kurulum, demo ve fiyatlandırma için doğrudan bize ulaşın. En hızlı yanıt telefonla gelir.',

  'contact.reach.title': 'Ulaşma yolları',
  'contact.phone.label': 'Telefon',
  'contact.phone.note': 'Hafta içi 09:00–18:00 (TRT).',
  'contact.email.label': 'E-posta',
  'contact.email.note': 'Genellikle aynı iş günü içinde yanıtlanır.',
  'contact.address.label': 'Adres',
  'contact.address.note': 'Ziyaret için önceden randevu almanızı rica ederiz.',

  'contact.demo.eyebrow': 'Demo talebi',
  'contact.demo.title': 'Kendi senaryonuzla görün.',
  'contact.demo.sub':
    'Demoyu kendi iş akışınız üzerinden kurgularız. Hazırlık için ihtiyacımız olan tek şey, otomatikleştirmek istediğiniz işin kabaca tarifi.',
  'contact.demo.item.1': 'Havuzların sizde nasıl görüneceği: hesaplar, modeller, sunucular.',
  'contact.demo.item.2': 'Bir işin uçtan uca yolu: karar, yürütme, kanıt, onay.',
  'contact.demo.item.3': 'Kurulum ve entegrasyon için gereken altyapı.',
  'contact.demo.subject': 'Kimene demo talebi',
  'contact.demo.cta': 'Demo talebi gönderin',
  'contact.demo.call': 'Telefonla arayın',

  /* ---- CTA ve altbilgi ----------------------------------------------- */
  'cta.title': 'Kendi sunucunuzda, kendi hesaplarınızla.',
  'cta.sub':
    'Kimene sizin altyapınızda çalışır ve sizin sağlayıcı hesaplarınızı yönetir. Kimlik bilgisi paylaşan bir servis değildir, çok kiracılı bir vekil değildir, sağlayıcı sınırlarını dolaşmanın bir yolu değildir.',
  'cta.primary': 'İletişime geçin',
  'cta.secondary': 'Telefonla arayın',
  'foot.providerTerms':
    'Kimene, operatörün kendi güvendiği profillerini yönetir. Sağlayıcıların kullanım koşullarına uymak operatörün sorumluluğundadır.',
  'foot.builtWith': 'Go, React ve tmux ile kuruldu.',
  'foot.siteHeading': 'Site haritası',
  'foot.contactHeading': 'İletişim',
  'foot.rights': 'Tüm hakları saklıdır.',
} as const
